import { useSyncExternalStore } from "react";
import { translateError, useT } from "../i18n/hooks.ts";
import { driveSyncStore } from "../model/driveSyncStore.ts";
import type { Language } from "../model/language.ts";
import type { Theme } from "../model/theme.ts";

function SunIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 2.5v3M12 18.5v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2.5 12h3M18.5 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M20 14.5A8.5 8.5 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5Z" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 13a7.7 7.7 0 0 0 0-2l2-1.6-2-3.4-2.4 1a7.6 7.6 0 0 0-1.7-1L15 3.5h-4l-.3 2.5a7.6 7.6 0 0 0-1.7 1l-2.4-1-2 3.4L6.6 11a7.7 7.7 0 0 0 0 2l-2 1.6 2 3.4 2.4-1a7.6 7.6 0 0 0 1.7 1L11 20.5h4l.3-2.5a7.6 7.6 0 0 0 1.7-1l2.4 1 2-3.4-2-1.6Z" />
    </svg>
  );
}

// Language autonyms — deliberately not in es.ts/en.ts, same as the {ES,EN}
// labels this list replaces: a language's own name is shown in itself, not
// translated (a Spanish speaker wants to read "English", not "Inglés").
const LANGUAGES: { value: Language; label: string }[] = [
  { value: "es", label: "Español" },
  { value: "en", label: "English" },
];

/**
 * Settings menu — theme, language and (informational only) which commit the
 * running build was deployed from, grouped behind one icon instead of
 * separate top-bar buttons that would keep growing every time an app-wide
 * setting is added. Replaces the old standalone ThemeToggle.tsx +
 * LanguageSelector.tsx pair. Commit comes from __COMMIT_SHA__, injected at
 * build time — see vite.config.ts.
 *
 * Controlled from outside (open/onToggle/onClose), same as
 * LanguageSelector.tsx used to be: TopBar.tsx folds it into its own popover
 * state, App.tsx's floating use (Landing/wizard) keeps its own local
 * boolean — each caller decides how "only one open at a time" applies to
 * its own context.
 */
export function SettingsMenu({
  theme,
  onToggleTheme,
  language,
  onChangeLanguage,
  open,
  onToggle,
  onClose,
  floating = false,
}: {
  theme: Theme;
  onToggleTheme: () => void;
  language: Language;
  onChangeLanguage: (language: Language) => void;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  /** On screens without a top bar (Landing, new-project wizard) it anchors to the top right. */
  floating?: boolean;
}) {
  const t = useT();
  const themeLabel = theme === "light" ? t("themeToggle.toDarkMode") : t("themeToggle.toLightMode");
  const shortSha = __COMMIT_SHA__ ? __COMMIT_SHA__.slice(0, 7) : null;
  const drive = useSyncExternalStore(driveSyncStore.subscribe, driveSyncStore.getSnapshot);

  return (
    <div style={{ position: "relative" }}>
      <button
        className={floating ? "button-icon floating-theme-toggle" : "button-icon"}
        onClick={onToggle}
        aria-label={t("settings.title")}
        title={drive.needsReconnect ? t("settings.driveNeedsReconnectTitle") : t("settings.title")}
      >
        <GearIcon />
        {drive.needsReconnect && <span className="badge" />}
      </button>
      {open && (
        <div className="popover menu-popover menu-popover-settings">
          <div className="settings-row">
            <span className="settings-row-label">{t("settings.theme")}</span>
            <button
              className="button-icon"
              onClick={onToggleTheme}
              aria-label={themeLabel}
              title={themeLabel}
            >
              {theme === "light" ? <MoonIcon /> : <SunIcon />}
            </button>
          </div>
          <div className="settings-row">
            <span className="settings-row-label">{t("common.language")}</span>
            <div className="settings-row-options">
              {LANGUAGES.map((l) => (
                <button
                  key={l.value}
                  className={l.value === language ? "button tab-active" : "button"}
                  onClick={() => {
                    onChangeLanguage(l.value);
                    onClose();
                  }}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>
          <div className="settings-row">
            <span className="settings-row-label">{t("settings.drive")}</span>
            {drive.connected ? (
              <button className="button-icon" onClick={() => driveSyncStore.disconnect()}>
                {t("settings.driveDisconnect")}
              </button>
            ) : (
              <button
                className="button-icon"
                disabled={drive.connecting}
                onClick={() => {
                  driveSyncStore.connect().catch(() => {
                    // Failure already reflected in the store's own snapshot (drive.lastError) — nothing more to do here.
                  });
                }}
              >
                {drive.connecting ? t("settings.driveConnecting") : t("settings.driveConnect")}
              </button>
            )}
          </div>
          {drive.lastError != null && <p className="error">{translateError(drive.lastError, t)}</p>}
          {shortSha && (
            <div className="settings-row">
              <span className="settings-row-label">{t("settings.version")}</span>
              <span className="settings-row-value">{shortSha}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
